import InteractionImageLanguages from "../asset_localization/InteractionImageLanguages";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
  selectReferenceLanguage,
  selectSequenceVariables,
  selectSelectedElement,
  selectSelectedElementType,
  updateSelectedInteraction,
  updateSelectedInteractionProperty,
  updateSelectedTakeSequence,
} from "../../app/slices/TrainingSlice";
import { selectLocalizedLanguages } from "../../app/slices/TrainingLocalizationSlice";
import { getKeysFromInteraction } from "../../helpers/KeysHelper";
import {
  closeQuizFeedback,
  openQuizFeedback,
  selectAdvancedTranslation,
  selectIsQuizFeedbackOpen,
  selectQuizFeedbackParentQuizId,
  setAdvancedTranslationEnabled,
  setAdvancedTranslationSelection,
  setFocusedSequenceNodeId,
} from "../../app/slices/GlobalSlice";
import { supportsQuizFeedback, isQuizFeedbackEnabled } from "../../models/QuizFeedbackHelper";
import { selectAllowWrongAnswersWhitelisted } from "../../app/slices/UserSlice";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { InteractionProperty } from "../../models/InteractionProperty";
import EditorColumn, {
  EditorColumnFieldGroup,
  EditorColumnHeader,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import InteractionPropertyView from "./InteractionPropertyView";
import InteractionWarningsView from "../validation/InteractionWarningsView";
import PiInputfield from "../../components/PiInputField";
import { useTranslation } from "react-i18next";
import {
  selectErrorsForInteraction,
  selectWarningsForInteraction,
} from "../../app/slices/ValidationSlice";
import InteractionErrorsView from "../validation/error_log/InteractionErrorsView";
import PiButton from "../../components/PiButton";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import useInteractionsTranslation, {
  TranslateInteractionOptions,
} from "./InteractionsTranslation.hook";
import AdvancedTranslationPanel, {
  AdvancedTranslationSelection,
} from "./AdvancedTranslationPanel";
import { CircularProgress, Tooltip } from "@mui/material";
import { Bounce, toast } from "react-toastify";
import { RootState } from "../../app/Store";
import SequenceOutliner from "../sequence/SequenceOutliner";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SequenceVariablesManager from "../sequence/variables/SequenceVariablesManager";
import CodeIcon from "@mui/icons-material/Code";
import {
  isBuiltInVariable,
} from "../../models/interactions/BuiltInSequenceVariables";
import VariableItem from "../sequence/variables/VariableItem";
import { ISequence } from "../../models/interactions/Sequence";
import SequenceCanvasActionsPanel from "./SequenceCanvasActionsPanel";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import {
  TRANSLATION_FAILED_KEY,
  TRANSLATION_FAILURE_TOAST_MS,
} from "../../helpers/translationFeedback";

export default function InteractionPropertiesColumn() {
  const interaction = useSelector(selectSelectedElement) as
    | Interaction
    | undefined;

  if (!interaction) return null;

  return <InteractionPropertiesColumnContent interaction={interaction} />;
}

function InteractionPropertiesColumnContent({
  interaction,
}: {
  interaction: Interaction;
}) {
  const sequenceVariables = useSelector(selectSequenceVariables);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedElementType = useSelector(selectSelectedElementType);

  const { translateAll } = useInteractionsTranslation();
  const warnings = useSelector((state: RootState) =>
    selectWarningsForInteraction(state, interaction.id)
  );

  const errors = useSelector((state: RootState) =>
    selectErrorsForInteraction(state, interaction.id)
  );

  const onChange = useCallback(
    (interactionProperty: InteractionProperty) => {
      dispatch(updateSelectedInteractionProperty(interactionProperty));
    },
    [dispatch]
  );

  const onUpdateInteraction = useCallback(
    (updatedInteraction: Interaction) => {
      dispatch(updateSelectedInteraction(updatedInteraction));
    },
    [dispatch]
  );

  const isAllowWrongAnswersWhitelisted = useSelector(
    selectAllowWrongAnswersWhitelisted
  );

  const interactionFactory = useMemo(() => {
    if (!interaction?.type) return undefined;
    return getInteractionFromFactory(interaction.type);
  }, [interaction?.type]);
  const propertiesEditor = interactionFactory?.PropertiesEditor;
  const propertiesColumnNotice = interactionFactory?.PropertiesColumnNotice;

  const visibleProperties = useMemo(() => {
    if (!interaction?.properties) return [];
    return interaction.properties.filter((property) => {
      if (propertiesEditor?.handledPropertyIds?.includes(property.id)) {
        return false;
      }
      if (
        property.id === "allowWrongAnswers" &&
        !isAllowWrongAnswersWhitelisted
      )
        return false;
      return true;
    });
  }, [
    interaction?.properties,
    isAllowWrongAnswersWhitelisted,
    propertiesEditor,
  ]);

  const customPropertiesView =
    propertiesEditor?.render(interaction, {
      updateInteraction: onUpdateInteraction,
      updateProperty: onChange,
    }) ?? null;

  const views = visibleProperties.map((property, index) => {
    return (
      <InteractionPropertyView
        key={index}
        interactionProperty={property}
        onChange={onChange}
      />
    );
  });

  // renaming
  const onRenameInteraction = (value: string) => {
    if (selectedElementType === "takeSequence") {
      dispatch(
        updateSelectedTakeSequence({
          ...(interaction as ISequence),
          name: value,
        })
      );
      return;
    }

    dispatch(updateSelectedInteraction({ ...interaction, name: value }));
  };

  const renameField = (
    <PiInputfield
      label={t("name")}
      value={interaction.name}
      placeholder={"Interaction name"}
      onChange={onRenameInteraction}
      disabled={interaction.type === InteractionType.DynamicInteraction}
      square
    />
  );

  const [isTranslationLoading, setIsTranslationLoading] = useState(false);
  const builtInVariables = useMemo(
    () => sequenceVariables.filter((variable) => isBuiltInVariable(variable)),
    [sequenceVariables]
  );

  const referenceLanguage = useSelector(selectReferenceLanguage);
  const localizedLanguages = useSelector(selectLocalizedLanguages);

  const interactionKeys = useMemo(
    () => getKeysFromInteraction(interaction, "key_") as string[],
    [interaction]
  );

  const storedAdvanced = useSelector(selectAdvancedTranslation);
  const isAdvancedMode = storedAdvanced.isEnabled;

  /**
   * The stored selection carries nulls until the editor changes something, so an
   * untouched panel still follows the training's reference language and language
   * list. Anything naming a language the training no longer has falls back to the
   * default rather than sending a request for it.
   */
  const advancedSelection: AdvancedTranslationSelection = useMemo(() => {
    const codes = localizedLanguages.map((language) => language.shortcode);
    const from =
      storedAdvanced.from && codes.includes(storedAdvanced.from)
        ? storedAdvanced.from
        : referenceLanguage;
    const defaults = codes.filter((shortcode) => shortcode !== from);
    const to = storedAdvanced.to
      ? storedAdvanced.to.filter(
          (shortcode) => shortcode !== from && codes.includes(shortcode)
        )
      : defaults;

    return { from, to, scope: storedAdvanced.scope };
  }, [storedAdvanced, localizedLanguages, referenceLanguage]);

  const setAdvancedSelection = useCallback(
    (next: AdvancedTranslationSelection) =>
      dispatch(setAdvancedTranslationSelection(next)),
    [dispatch]
  );

  /**
   * The confirmation waits for the run to finish, and a failure says so. Both
   * used to be impossible: the hook resolved before any language had landed, and
   * the service swallowed its own errors.
   */
  const runTranslation = async (
    options: TranslateInteractionOptions,
    successKey: string
  ) => {
    setIsTranslationLoading(true);
    try {
      await translateAll(interaction, options);
      // No autoClose: the editor's ToastContainer already dismisses after 1s,
      // which is the right length for a confirmation you asked for.
      toast.success(t(successKey) as string, {
        position: "top-right",
        closeOnClick: true,
        theme: "light",
        transition: Bounce,
      });
    } catch (error) {
      toast.error(t(TRANSLATION_FAILED_KEY) as string, {
        position: "top-right",
        // Deliberately longer than the 1s default: a failure the editor blinks
        // past is how they end up shipping a training with gaps.
        autoClose: TRANSLATION_FAILURE_TOAST_MS,
        closeOnClick: true,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setIsTranslationLoading(false);
    }
  };

  const runPlainTranslation = (missingOnly: boolean) =>
    runTranslation(
      { scope: missingOnly ? "missing" : "all" },
      missingOnly
        ? "interaction_translation.missing_updated"
        : "interaction_translation.all_updated"
    );

  const runAdvancedTranslation = () =>
    runTranslation(
      {
        from: advancedSelection.from,
        to: advancedSelection.to,
        scope: advancedSelection.scope,
      },
      advancedSelection.scope === "missing"
        ? "interaction_translation.missing_updated"
        : "interaction_translation.all_updated"
    );

  const renderAdvancedToggle = (expand: () => void) => (
    <button
      type="button"
      className={classNames(
        "w-[22px] h-[22px] inline-flex items-center justify-center rounded-5 border cursor-pointer text-[14px] transition-colors",
        isAdvancedMode
          ? "bg-creator-childHover border-creator-primary text-creator-primary"
          : "bg-transparent border-transparent text-creator-text-sub hover:bg-creator-surfaceHover"
      )}
      aria-pressed={isAdvancedMode}
      aria-label={t("interaction_translation.advanced_toggle") as string}
      title={t("interaction_translation.advanced_toggle") as string}
      data-testid="advanced-translation-toggle"
      onClick={() => {
        // Switching the mode on inside a collapsed section would otherwise look
        // like nothing happened.
        expand();
        dispatch(setAdvancedTranslationEnabled(!isAdvancedMode));
      }}
    >
      <TuneOutlinedIcon fontSize="inherit" />
    </button>
  );

  const translateButton = (
    <div className="w-full h-[36px] max-h-[36px]">
      <PiButton
        variant="piBlueOutlined"
        onClick={() => runPlainTranslation(false)}
        data-testid="translate-all-button"
      >
        {t("interaction_translation.translate_all") as string}
      </PiButton>
    </div>
  );

  const isQuizFeedbackOpen = useSelector(selectIsQuizFeedbackOpen);
  const quizFeedbackParentQuizId = useSelector(selectQuizFeedbackParentQuizId);
  const isThisQuizFeedbackOpen =
    isQuizFeedbackOpen && quizFeedbackParentQuizId === interaction.id;

  const feedbackEnabled = isQuizFeedbackEnabled(interaction);
  const quizFeedbackButton = supportsQuizFeedback(interaction) && (
    <Tooltip
      title={feedbackEnabled ? "" : t("quiz_feedback_open_answers_disabled")}
    >
      <div className="w-full h-[36px] max-h-[36px]">
        <PiButton
          variant="piBlueOutlined"
          onClick={() =>
            dispatch(
              isThisQuizFeedbackOpen
                ? closeQuizFeedback()
                : openQuizFeedback(interaction.id)
            )
          }
          disabled={!feedbackEnabled}
          data-testid="quiz-feedback-button"
        >
          {t("quiz_feedback") as string}
        </PiButton>
      </div>
    </Tooltip>
  );

  const translateMissingButton = (
    <div className="w-full h-[36px]">
      <PiButton
        variant="piBlueOutlined"
        onClick={() => runPlainTranslation(true)}
        data-testid="translate-missing-button"
      >
        {t("interaction_translation.translate_missing") as string}
      </PiButton>
    </div>
  );

  return (
    <EditorColumn
      testId="interaction-properties-column"
      widthClassName="w-full min-w-0"
      borderSide="left"
      className="interactionpropertiescontent-root"
      header={
        <EditorColumnHeader
          icon={<TouchAppOutlinedIcon fontSize="inherit" />}
          title={interaction?.name}
        />
      }
    >
      {errors?.length > 0 && (
        <InteractionErrorsView
          errors={errors}
          onErrorClick={(nodeId) => {
            if (nodeId) {
              dispatch(setFocusedSequenceNodeId(nodeId));
            }
          }}
        />
      )}
      {warnings?.length > 0 && (
        <InteractionWarningsView warnings={warnings} />
      )}
      {propertiesColumnNotice}
      <EditorColumnSection
        title="Interaction"
        icon={<TouchAppOutlinedIcon fontSize="inherit" />}
        defaultExpanded
        testId="section-Interaction"
      >
        <EditorColumnFieldGroup>{renameField}</EditorColumnFieldGroup>
      </EditorColumnSection>
      {(customPropertiesView || views.length > 0) && (
        <EditorColumnSection
          title="Properties"
          icon={<SettingsOutlinedIcon fontSize="inherit" />}
          defaultExpanded
          testId="section-Properties"
        >
          <EditorColumnFieldGroup>
            {customPropertiesView}
            {views}
          </EditorColumnFieldGroup>
        </EditorColumnSection>
      )}

      {/* Renders nothing for interactions without localizable images. */}
      <InteractionImageLanguages interaction={interaction} />

      <EditorColumnSection
        title={t("interaction_translation.title") as string}
        icon={<GTranslateOutlinedIcon fontSize="inherit" />}
        defaultExpanded
        testId="section-Translation"
        renderAction={({ expand }) =>
          renderAdvancedToggle(expand)
        }
      >
        {isAdvancedMode ? (
          // A swap, not an addition: while a narrowed scope is on screen there
          // must not also be a button that says it translates everything.
          <AdvancedTranslationPanel
            selection={advancedSelection}
            onChange={setAdvancedSelection}
            interactionKeys={interactionKeys}
            isTranslating={isTranslationLoading}
            onTranslate={runAdvancedTranslation}
          />
        ) : (
          <EditorColumnFieldGroup>
            {isTranslationLoading && (
              <div className="w-full h-[36px] flex items-center justify-center text-creator-primary">
                <CircularProgress color="inherit" />
              </div>
            )}
            {!isTranslationLoading && translateButton}
            {!isTranslationLoading && translateMissingButton}
          </EditorColumnFieldGroup>
        )}
        <EditorColumnFieldGroup>{quizFeedbackButton}</EditorColumnFieldGroup>
      </EditorColumnSection>
      {interaction.type === InteractionType.Sequence && (
        <>
          <EditorColumnSection
            title="Outliner"
            icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
            defaultExpanded
            testId="section-Outliner"
          >
            <SequenceOutliner />
          </EditorColumnSection>
          <SequenceCanvasActionsPanel />
          {builtInVariables.length > 0 && (
            <EditorColumnSection
              title="Built-in Variables"
              icon={<CodeIcon fontSize="inherit" />}
              testId="section-Built-in Variables"
            >
              <div className="flex flex-col gap-1">
                {builtInVariables.map((variable) => (
                  <VariableItem
                    key={variable.id}
                    variable={variable}
                    isEditing={false}
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                    existingVariables={sequenceVariables}
                  />
                ))}
              </div>
            </EditorColumnSection>
          )}
          <EditorColumnSection
            title="Variables"
            icon={<CodeIcon fontSize="inherit" />}
            defaultExpanded
            testId="section-Variables"
          >
            <SequenceVariablesManager
              includeBuiltIns={false}
              showSectionHeaders={false}
            />
          </EditorColumnSection>
        </>
      )}
    </EditorColumn>
  );
}
